import { useStyles } from '@webapps/styles';
import { Button, Grid } from '@material-ui/core';
import { FormikAutocompletePx, FormikTextFieldPx, TextFieldPx, useMutation, useQuery, useToast } from '@webapps/reactjs-tech-components';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { getAddress, getJobs, createBusiness } from '@webapps/business';
import { useState, useRef, useEffect } from 'react';
import { useFormik } from 'formik';

const AddBusiness = ({ opcoRecord, handleValidCount }) => {
  const {
    classes,
    css,
    cx,
    global,
    theme: { spacing },
  } = useStyles(styles);

  const { addToast } = useToast();

  const [addresses, setAddresses] = useState([]);
  const [phone, setPhone] = useState('');
  const [errorPhone, setErrorPhone] = useState(false);
  const [phoneClasses, setPhoneClasses] = useState('phoneInput');
  const [keywordRequest, setKeywordRequest] = useState('');
  const phoneClassesRef = useRef();
  phoneClassesRef.current = phoneClasses;

  const {
    error: errorRegistrationBusiness,
    isLoading: mutateCreateBusinessLoading,
    isSuccess: mutateCreateBusinessSuccess,
    mutate: mutateCreateBusiness,
  } = useMutation(createBusiness, {
    onError: async (error) => {
      if (error.request.status === 409) {
        setErrorMessage(
          "L'e-mail ou le numéro de téléphone utilisé est déjà associé à un compte."
        );
      }
    },
  });

  function generatePassword() {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const allChars = uppercase + lowercase + numbers;

    // Ensure at least one of each required type
    const passwordArray = [
      uppercase[Math.floor(Math.random() * uppercase.length)],
      lowercase[Math.floor(Math.random() * lowercase.length)],
      numbers[Math.floor(Math.random() * numbers.length)]
    ];

    // Fill the rest of the password length with random characters
    for (let i = 3; i < 12; i++) {
      passwordArray.push(allChars[Math.floor(Math.random() * allChars.length)]);
    }

    // Shuffle the password array to avoid predictable pattern
    for (let i = passwordArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]];
    }

    return passwordArray.join('');
  }

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      name: "",
      siret: "",
      phone: "",
      address: "",
      city: "",
      code: "",
      subscription: "free",
      knowUs: null,
      opco: "",
      ogr: ""
    },
    onSubmit: (values) => {
      values.opco = opcoRecord?.id;
      values.password = generatePassword();
      console.log('Données du formulaire:', values);

      if (!isValidPhoneNumber(values.phone)) {
        addToast({
          status: 'error',
          message: 'Numéro de téléphone invalide',
        });
        return;
      }

      mutateCreateBusiness(values);
    },
  });

  useEffect(() => {
    if (mutateCreateBusinessSuccess) {
      addToast({
        status: 'success',
        message: 'Entreprise ajoutée avec succès !',
      });
      formik.resetForm();
    }
    if (errorRegistrationBusiness) {
      addToast({
        status: 'error',
        message: errorRegistrationBusiness?.response?.data?.description || 'Une erreur est survenue lors de l\'ajout de l\'entreprise.',
      });
    }
  }, [mutateCreateBusinessSuccess, errorRegistrationBusiness]);


  const getAdresses = useMutation(getAddress, {
    onSuccess: (data) => {
      setAddresses(data?.data?.features);

      // Auto-complétion de la première adresse trouvée
      if (data?.data?.features?.[0]) {
        const firstAddress = data.data.features[0];
        formik.setFieldValue('address', firstAddress.properties.label);
        formik.setFieldValue('city', firstAddress.properties.city);
        formik.setFieldValue('code', firstAddress.properties.postcode);
      }
    },
  });

  const { data: { content: jobs = [] } = {} } = useQuery(getJobs, {
    key: ['jobs', keywordRequest],
    variables: { keyword: keywordRequest || '' },
  });

  const handleChangeAddress = async (searchValue) => {
    if (!searchValue.target.value || searchValue.target.value.length < 3) return;
    await getAdresses.mutateAsync({ name: searchValue.target.value });
  };

  const handleChangePhone = (phoneInfo) => {
    if (phoneInfo) {
      setPhone(phoneInfo);
      formik.setFieldValue('phone', phoneInfo);

      if (isValidPhoneNumber(phoneInfo)) {
        setErrorPhone(false);
        setPhoneClasses('phoneInput');
      } else {
        setErrorPhone(true);
        setPhoneClasses('phoneInputError');
      }
    }
  };

  const handlePhoneBlur = () => {
    if (phoneClassesRef.current === 'phoneInputError') {
      addToast({
        status: 'error',
        message: 'Numéro de téléphone invalide',
      });
    }
  };

  const transformAndValidateSiret = (inputValue) => {
    // Use a regular expression to remove non-numeric characters
    const numericValue = inputValue.replace(/\D/g, '');

    // Limit the length to 14 characters
    const truncatedValue = numericValue.slice(0, 14);

    return truncatedValue;
  };

  const handleSiretChange = (event, formik) => {
    const inputValue = event.target.value;
    const transformedValue = transformAndValidateSiret(inputValue);

    // Update the formik field value
    formik.setFieldValue('siret', transformedValue);
  };

  // Fonction pour déterminer si un champ a une erreur
  const hasFieldError = (fieldName) => {
    const value = formik.values[fieldName];
    const error = formik.errors[fieldName];
    const touched = formik.touched[fieldName];

    // Vérifier les erreurs de validation Formik
    if (touched && error) return true;

    // Vérifier si c'est un champ requis et vide
    const requiredFields = ['name', 'siret', 'email', 'address', 'phone', 'ogr'];
    if (requiredFields.includes(fieldName)) {
      return value == null || value === '' || (typeof value === 'number' && value === 0);
    }

    return false;
  };

  // Gestion spéciale pour le téléphone
  const hasPhoneError = () => {
    return errorPhone || hasFieldError('phone');
  };

  return (
    <div className={classes.container}>
      <Grid container spacing={3}>
        <Grid
          item
          xs={4}
          className={hasFieldError('name') ? global.formikTextFieldWhiteError : global.formikTextFieldWhite}
        >
          <FormikTextFieldPx
            formik={formik}
            label="Nom de l'entreprise"
            name="name"
            variant="outlined"
            fullWidth
            borderRadius={6}
          />
        </Grid>

        <Grid
          item
          xs={4}
          className={hasFieldError('siret') ? global.formikTextFieldWhiteError : global.formikTextFieldWhite}
        >
          <FormikTextFieldPx
            formik={formik}
            fullWidth
            borderRadius={6}
            onChange={(event) => handleSiretChange(event, formik)}
            value={formik.values.siret || ''}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 14,
            }}
            label="SIRET"
            name="siret"
          />
        </Grid>

        <Grid
          item
          xs={4}
          className={hasFieldError('email') ? global.formikTextFieldWhiteError : global.formikTextFieldWhite}
        >
          <FormikTextFieldPx
            formik={formik}
            label="Email"
            name="email"
            variant="outlined"
            fullWidth
            borderRadius={6}
          />
        </Grid>

        <Grid
          item
          xs={4}
          className={hasFieldError('address') ? global.formikTextFieldWhiteError : global.formikTextFieldWhite}
        >
          <Autocomplete
            autoComplete={false}
            disableUnderLine="true"
            fullWidth
            classes={{
              root: css({
                height: '100%',
              }),
            }}
            getOptionLabel={(option) => option || ''}
            options={addresses?.map(
              (option) => option?.properties?.label
            )}
            renderInput={(params) => (
              <TextFieldPx
                {...params}
                label="Adresse"
                name="address"
                fullWidth
                inputPadding={"14px 12px 0px 6px"}
                borderRadius={6}
                classes={{
                  root: css({
                    flexDirection: 'row',
                  }),
                }}
                className={css({
                  padding: '0 0 0 8px !important',
                  '.MuiAutocomplete-input': {
                    padding: '0 0 0 8px !important'
                  },
                })}
                onChange={(value) => {
                  handleChangeAddress(value);
                }}
                value={formik.values.address}
                variant="outlined"
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            )}
            value={formik.values.address.label}
            onChange={(event, newValue) => {
              if (newValue) {
                formik.setFieldValue('address', newValue);
              }
            }}
            size="small"
          />
        </Grid>

        <Grid
          item
          xs={4}
          className={hasFieldError('ogr') ? global.formikTextFieldWhiteError : global.formikTextFieldWhite}
        >
          <FormikAutocompletePx
            defaultValue=""
            formik={formik}
            borderRadius={6}
            fullWidth
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) =>
              value.ogr === option.ogr
            }
            label="Indiquer l'intitulé du poste"
            name="ogr"
            onInputChange={(event, value) => {
              setKeywordRequest(value);
            }}
            options={jobs}
            pathValue="ogr"
            value={
              jobs.find(
                ({ ogr }) =>
                  ogr === formik.values.ogr
              ) || null
            }
          />
        </Grid>

        <Grid
          item
          xs={4}
          className={hasPhoneError() ? global.formikTextFieldWhiteError : global.formikTextFieldWhite}
        >
          <PhoneInput
            className={`${classes[phoneClassesRef.current]}`}
            countryCallingCodeEditable={false}
            defaultCountry="FR"
            international
            limitMaxLength
            name="phone"
            onChange={(phone) => {
              handleChangePhone(phone);
            }}
            onBlur={handlePhoneBlur}
            value={formik.values.phone}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={formik.handleSubmit}
          >
            Ajouter l'entreprise
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

const styles = ({ spacing }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
  },
  text: {
    fontSize: 14,
    fontWeight: 500,
    color: "#0060F9",
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
  },
  phoneInput: {
    height: '100%',
    '& > *': {
      minHeight: '45px',
      backgroundColor: '#F3F7FF',
      borderRadius: 5,
      padding: spacing(0, 2),
      height: '100%',
      color: '#152652',
      border: '1px #C0D9FF solid',
    },
  },
  phoneInputError: {
    height: '100%',
    '& > *': {
      minHeight: '45px',
      backgroundColor: '#F3F7FF',
      borderRadius: 5,
      padding: spacing(0, 2),
      height: '100%',
      color: '#f44336',
      border: '1px red solid',
    },
  },
  button: {
    backgroundColor: "#0060F9",
    color: "white",
    fontWeight: 500,
    borderRadius: "6px",
    height: "42px",
    "&:hover": {
      backgroundColor: "#0050D9",
    },
  },
});

export default AddBusiness; 